'use no memo';

import {AutoLinkNode, LinkNode} from '@lexical/link';
import {LexicalEditor, LexicalNode, TextNode} from 'lexical';
import {
  $isBeautifulMentionNode,
  BeautifulMentionComponentProps,
  BeautifulMentionsMenuItemProps,
  createBeautifulMentionNode,
} from 'lexical-beautiful-mentions';
import {DOMExportOutputMap} from 'lexical/LexicalNode';
import {
  Button,
  CustomMenuItem,
  FormControl,
  FormField,
  showNotification,
  StyledContentEditable,
  TextInput,
  Tooltip,
  useDialog,
} from 'platform/components';
import {Box, Hide, HStack, Show, VStack} from 'platform/foundation';
import styled from 'styled-components';
import {match} from 'ts-pattern';

import {forwardRef, useState} from 'react';
import {UseFormReturn, useWatch} from 'react-hook-form';

import {isNotNil} from 'ramda';

import {TemplateTypeEnum} from '@dms/api/core';
import {i18n} from '@dms/i18n';
import {testIds} from '@dms/routes';
import {SettingsFooter} from '@dms/settings/shared';
import {
  AiComposerEditor,
  AiComposerAppliedStatus,
  AiMessageComposerPanel,
  AiComposerSelectionActions,
  getAiComposerEditorHighlights,
  EMPTY_PLACEHOLDER,
  replaceVariables,
  toAiComposerPlaceholders,
  useAiMessageComposer,
  usePermissions,
  useTenant,
  VARIABLE_REGEX,
} from '@dms/shared';

import {usePrevious} from 'shared/web';

import {useContextParameters} from '../hooks/useContextParameters';
import {MessageTemplateFormValues} from '../types/MessageTemplateFormValues';
import {getContextOptions} from '../utils/getContextOptions';
import {MessagePreviewDialog} from './MessagePreviewDialog';

const PLACEHOLDER_SPLIT_REGEX = /(\{\{\s*[\w.]+\s*\}\})/g;

type Props = {
  formApi: UseFormReturn<MessageTemplateFormValues>;
  control: FormControl<MessageTemplateFormValues>;
  templateType: TemplateTypeEnum;
  isMutationInProgress: boolean;
  onDiscard: () => void;
  isLocaleFieldDisabled?: boolean;
};

const ContentEditableComponent = styled(StyledContentEditable)<{$isMessage: boolean}>`
  .mentions {
    display: inline-block;
    background-color: ${({theme}) => theme.colors.palettes.blue[30][100]};
    border-radius: ${({theme}) => theme.radii.large};
    padding: ${({theme}) => theme.getSize(1)} ${({theme}) => theme.getSize(2)};
    transition: all 250ms;

    &:hover {
      background-color: ${({theme}) => theme.colors.palettes.blue[40][100]};
    }
  }
`;

const ContentEditableComponentMessage = styled(ContentEditableComponent)`
  min-height: ${({theme}) => theme.getSize(25)};
`;

// This is needed because Tooltip component wrap itself into <div>
// eslint-disable-next-line eag/no-css-property -- Muted pre-existing violation
const CustomMentionWrapper = styled.span`
  & > div {
    display: inline-block;
  }
`;

interface MenuItemComponentProps extends BeautifulMentionsMenuItemProps {
  ref?: React.Ref<HTMLLIElement>;
}

const MenuItemComponent = ({selected, ref, item, ...props}: MenuItemComponentProps) => {
  const component = <CustomMenuItem ref={ref} item={item} selected={selected} {...props} />;

  if (selected) {
    return (
      <Tooltip label={item.data?.description} isOpen placement="right" offset={14}>
        {component}
      </Tooltip>
    );
  }

  return component;
};

const CustomBeautifulMentionComponent = forwardRef<
  HTMLSpanElement,
  BeautifulMentionComponentProps<{id: string; description: string}>
>(({data, value, ...other}, ref) => (
  <CustomMentionWrapper ref={ref} {...other}>
    <Tooltip label={data?.description} placement="top">
      {value}
    </Tooltip>
  </CustomMentionWrapper>
));

const CustomMention = createBeautifulMentionNode(CustomBeautifulMentionComponent);

const linkFormatter = (_editor: LexicalEditor, node: LexicalNode) => {
  const convertedNode = node as AutoLinkNode;
  const element = document.createTextNode(convertedNode.getURL());
  return {element};
};

const exportHtmlNodeTransforms = (nodes: DOMExportOutputMap) => {
  nodes.set(CustomMention[0], (_editor, node) => {
    const data = $isBeautifulMentionNode(node) ? node.getData() : undefined;
    const literal = data?.literal;
    // A placeholder loaded from the template keeps its original bytes; a new one is canonical.
    const element = document.createTextNode(
      typeof literal === 'string' ? literal : `{{ ${data?.code} }}`
    );
    return {element};
  });
  nodes.set(AutoLinkNode, linkFormatter);
  nodes.set(LinkNode, linkFormatter);

  return nodes;
};

export const MessageTemplateFields = (props: Props) => {
  const [canPatchMessageTemplate] = usePermissions({
    permissionKeys: ['patchMessageTemplate'],
  });
  const {isMultiLocale, supportedLocales} = useTenant();
  const isLocaleFieldShown = isMultiLocale;
  const [isOpen, onOpen, onClose] = useDialog(false);
  const context = useWatch({control: props.control, name: 'context'});
  const previousContext = usePrevious(context);

  const locale = useWatch({control: props.control, name: 'locale'});

  const {contextParameters, isLoading} = useContextParameters(context);
  const transformStringToNodes = (value: string | null) => {
    if (!value) {
      return [];
    }
    return value
      .split(PLACEHOLDER_SPLIT_REGEX)
      .filter((part) => part.length > 0)
      .map((part) => {
        const code = part.match(VARIABLE_REGEX)?.[1];
        const contextParameter = isNotNil(code)
          ? contextParameters.find((param) => param.code === code)
          : undefined;
        // An unknown placeholder stays literal text so it survives the editor round trip.
        return isNotNil(contextParameter)
          ? new CustomMention[0]('/', contextParameter.label, {...contextParameter, literal: part})
          : new TextNode(part);
      });
  };

  const suggestionInputProps = {
    placeholder: i18n.t('page.messageTemplates.labels.placeholderHint'),
    prefix: '/',
    options: contextParameters,
    MenuItemComponent,
    exportHtmlNodeTransforms,
    transformStringToNodes,
    CustomMention,
  };

  const onOpenPreview = () => {
    onOpen();
  };
  const onContextChange = (context: string | number | null) => {
    if (previousContext !== context) {
      const formValues = props.formApi.getValues();
      props.formApi.setValue('subject', replaceVariables(formValues.subject || '', ''));
      props.formApi.setValue('body', replaceVariables(formValues.body || '', ''));

      if (VARIABLE_REGEX.test(formValues.subject) || VARIABLE_REGEX.test(formValues.body)) {
        showNotification.info(i18n.t('page.messageTemplates.notification.placeholdersCleared'));
      }
    }
  };

  const localeLabel = locale
    ? (supportedLocales.find((item) => item.value === locale)?.label ??
      `${i18n.t(`entity.tenant.labels.language-${locale?.split('-')[0]}`)} (${locale})`)
    : EMPTY_PLACEHOLDER;

  const [body, subject] = useWatch({control: props.control, name: ['body', 'subject']});
  const [bodyEditor, setBodyEditor] = useState<AiComposerEditor>(null);
  const channel = match(props.templateType)
    .with('email', () => 'email' as const)
    .with('sms', () => 'sms' as const)
    .with('chat', () => 'chat' as const)
    .exhaustive();

  const composer = useAiMessageComposer({
    channel,
    bodyFormat: 'PLAIN_TEXT',
    draft: {
      body: body ?? '',
      subject: channel === 'email' ? (subject ?? undefined) : undefined,
    },
    locale,
    placeholders: toAiComposerPlaceholders(contextParameters),
    editor: bodyEditor,
    onApply: (draft) => {
      props.formApi.setValue('body', draft.body);
      if (isNotNil(draft.subject)) {
        props.formApi.setValue('subject', draft.subject);
      }
    },
  });

  return (
    <VStack spacing={6}>
      <FormField
        name="name"
        type="text"
        control={props.control}
        label={i18n.t('page.messageTemplates.labels.templateName')}
        data-testid={testIds.settings.messageTemplates('templateName')}
        isRequired
      />
      <Show when={isLocaleFieldShown}>
        <Show when={props.isLocaleFieldDisabled}>
          <TextInput
            isDisabled
            value={localeLabel}
            data-testid={testIds.settings.messageTemplates('locale')}
            label={i18n.t('general.labels.locales')}
            helperText={i18n.t('page.messageTemplates.labels.localeReadOnlyHint')}
            isRequired
          />
        </Show>

        <Hide when={props.isLocaleFieldDisabled}>
          <FormField
            name="locale"
            type="choice"
            control={props.control}
            options={supportedLocales}
            data-testid={testIds.settings.messageTemplates('locale')}
            isNotClearable
            label={i18n.t('general.labels.locales')}
            isRequired
          />
        </Hide>
      </Show>
      <FormField
        name="context"
        type="choice"
        onChange={onContextChange}
        options={getContextOptions(props.templateType)}
        control={props.control}
        label={i18n.t('page.messageTemplates.labels.context')}
        data-testid={testIds.settings.messageTemplates('context')}
        isRequired
      />
      <Show when={props.templateType === 'email'}>
        <FormField
          name="subject"
          type="suggestionInput"
          control={props.control}
          label={i18n.t('page.messageTemplates.labels.subject')}
          data-testid={testIds.settings.messageTemplates('subject')}
          ContentEditableComponent={ContentEditableComponent}
          isLoadingOptions={isLoading}
          {...suggestionInputProps}
          isRequired
        />
      </Show>
      <FormField
        name="body"
        type="suggestionInput"
        control={props.control}
        label={i18n.t('page.messageTemplates.labels.message')}
        data-testid={testIds.settings.messageTemplates('message')}
        ContentEditableComponent={ContentEditableComponentMessage}
        isLoadingOptions={isLoading}
        {...suggestionInputProps}
        onEditorReady={setBodyEditor}
        selectionContent={
          composer.isEnabled ? (
            <AiComposerSelectionActions
              controller={composer}
              data-testid={testIds.settings.messageTemplates('aiComposer')}
            />
          ) : undefined
        }
        editorHighlights={getAiComposerEditorHighlights(composer)}
        onKeyDownCapture={composer.onEditorKeyDownCapture}
        labelActions={isNotNil(composer.labelAction) ? [composer.labelAction] : []}
        footerContent={
          <AiComposerAppliedStatus
            controller={composer}
            data-testid={testIds.settings.messageTemplates('aiComposer')}
          />
        }
        editorContent={
          <AiMessageComposerPanel
            controller={composer}
            data-testid={testIds.settings.messageTemplates('aiComposer')}
          />
        }
        isRequired
      />
      <Box>
        <HStack spacing={2}>
          <Button
            variant="secondary"
            title={i18n.t('page.messageTemplates.labels.previewButton')}
            isDisabled={!props.formApi.formState.isValid}
            data-testid={testIds.settings.messageTemplates('previewButton')}
            onClick={onOpenPreview}
          />
        </HStack>
      </Box>
      <Show when={canPatchMessageTemplate}>
        <SettingsFooter
          actions={[
            {
              type: 'button',
              title: i18n.t('general.actions.discard'),
              onClick: props.onDiscard,
              variant: 'secondary',
              'data-testid': testIds.settings.messageTemplates('discard'),
            },
            {
              type: 'form-button',
              control: props.control,
              buttonType: 'submit',
              isLoading: props.isMutationInProgress,
              title: i18n.t('general.actions.save'),
              'data-testid': testIds.settings.messageTemplates('save'),
            },
          ]}
        />
      </Show>
      <MessagePreviewDialog
        isOpen={isOpen}
        templateType={props.templateType}
        formApi={props.formApi}
        onClose={onClose}
      />
    </VStack>
  );
};
